// GitLab-style activity feed for the focal issue. Notes + state/milestone
// resource events, merged in chronological order. Separate from the
// `IssueTimeline` (which is the Loom methodology / stage progression
// view); the two coexist on the IssuePage as distinct strips.

import type { JSX } from "react";

import type {
  IssueActivityEvent,
  IssueActivityKind,
} from "@loom/shared/dashboard";

import { useT } from "../../i18n/index.js";
import { useRelativeTime } from "../../i18n/format.js";

import { Icon, type IconName } from "./Icon";

const ICON_FOR_KIND: Record<IssueActivityKind, IconName> = {
  note: "chat",
  system_note: "sliders",
  state_change: "flag",
  label: "plus",
  milestone: "sliders",
};

export interface IssueActivityTimelineProps {
  readonly events: ReadonlyArray<IssueActivityEvent>;
  readonly loading: boolean;
}

export const IssueActivityTimeline = ({
  events,
  loading,
}: IssueActivityTimelineProps): JSX.Element => {
  const t = useT();
  const rel = useRelativeTime();
  const formatTime = (iso: string): string => {
    const ms = Date.parse(iso);
    if (!Number.isFinite(ms)) return iso;
    return rel(ms);
  };
  if (loading) {
    return (
      <div className="issue-activity-timeline">
        <div className="issue-activity-empty">{t("dashboard.issueActivity.loading")}</div>
      </div>
    );
  }
  if (events.length === 0) {
    return (
      <div className="issue-activity-timeline">
        <div className="issue-activity-empty">{t("dashboard.issueActivity.empty")}</div>
      </div>
    );
  }
  return (
    <div className="issue-activity-timeline">
      {events.map((ev, idx) => (
        <div
          key={`${ev.at}-${ev.kind}-${idx}`}
          className={`issue-activity-row kind-${ev.kind}`}
        >
          <span className="issue-activity-time">{formatTime(ev.at)}</span>
          <span className="issue-activity-icon">
            <Icon name={ICON_FOR_KIND[ev.kind]} size={11} />
          </span>
          <div className="issue-activity-body">
            <div className="issue-activity-line">
              {ev.actor && (
                <span className="issue-activity-actor">@{ev.actor}</span>
              )}
              {ev.kind === "note" ? (
                <span className="issue-activity-note-body">{ev.text}</span>
              ) : (
                <span className="issue-activity-text">{ev.text}</span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
